<template>
  <ol>
    <template v-for="(group, i) in itemGroups">
      <!-- Item groups (accordion) -->
      <m-accord
        v-if="group && Array.isArray(group.items)"
        :key="`ItemGroup${i}`"
        class="gp"
        :toggled="expandedGroups[i]"
      >
        <!-- Item group accordion button -->
        <b-select
          slot="button"
          class="gp-button"
          :tickable="groupSelectable"
          :selected="groupSelectedCounts[i] === getTotalItemsCount(group.items)"
          :sub-count="groupSelectedCounts[i]"
          :title="group.title"
          @click="toggleExpandGroup(i)"
          @tick="toggleSelectGroup(i)"
        />
        <!-- Items -->
        <ol slot="items">
          <!-- To shorten page load time in slower devices,
               only render item group items
               after first user click -->
          <template v-if="canRenderGroups[i]">
            <template v-for="(item, j) in group.items">
              <li v-if="item && item.value && item.title" :key="`ItemGroup${i}Item${j}`" class="i-l__item">
                <!-- Subtitle item -->
                <h4 v-if="item.isSubtitle" class="i-l__item-subtitle">{{ item.title }}</h4>
                <!-- Select item -->
                <b-select
                  v-else
                  class="gp-button"
                  :title="item.title"
                  :selected="isItemSelected(item.value)"
                  @click="toggleSelectItem(i, item.value)"
                />
              </li>
            </template>
          </template>
        </ol>
      </m-accord>
    </template>
  </ol>
</template>

<script>
import AccordionMenu from '../../menu/accordionMenu'
import InputMixin from '../../../mixins/input'
import SelectButton from '../../button/selectButton'
import { ToggleModes } from '../../../libs/select'

export default {
  components: {
    'b-select': SelectButton,
    'm-accord': AccordionMenu,
  },
  created() {
    // Init number of selected items
    // for each group
    this.initGroupSelectedCounts()

    // Init expanded state for each group
    this.initGroupExpansions()

    // Init render state for each group
    this.initGroupRenderStates()
  },
  data() {
    return {
      /**
       * Whether each list item group
       * can be rendered
       */
      canRenderGroups: [],
      /**
       * Whether each list item group
       * is expanded
       */
      expandedGroups: [],
      /**
       * Number of selected items
       * for each list item group
       */
      groupSelectedCounts: [],
      /**
       * Input type must be "list-select"
       */
      type: 'list-select',
    }
  },
  methods: {
    /**
     * Get index of provided item
     * in list of selected items
     * @returns {number}
     */
    getItemSelectedIndex(item) {
      // If item provided,
      // get its index in selected list
      // using provided matcher function
      if (item) {
        return this.inputValue.findIndex((selected) => this.matcher(selected, item))
      }
      return -1
    },

    /**
     * Get total items count without subtitle
     * @returns {number}
     */
    getTotalItemsCount(items) {
      return items.filter((item) => !item.isSubtitle).length
    },

    /**
     * Set expanded state to false
     * for each list item group
     */
    initGroupExpansions() {
      this.expandedGroups = this.itemGroups.map(() => false)
    },

    /**
     * Set render state to false
     * for each list item group
     */
    initGroupRenderStates() {
      this.canRenderGroups = this.itemGroups.map(() => false)
    },

    /**
     * Calculate number of selected items
     * for each list item group
     */
    initGroupSelectedCounts() {
      this.groupSelectedCounts = this.itemGroups.map((group) => {
        if (group && Array.isArray(group.items)) {
          return group.items.reduce(
            (selectedCount, item) => selectedCount + (item && this.isItemSelected(item.value)),
            0,
          )
        }
        return 0
      })
    },

    /**
     * Whether provided item is selected
     */
    isItemSelected(item) {
      return this.getItemSelectedIndex(item) >= 0
    },

    /**
     * Toggle list item group accordion
     * based on provided group index
     * @param {number} groupIndex
     */
    toggleExpandGroup(groupIndex) {
      // Toggle group expansion
      this.$set(this.expandedGroups, groupIndex, !this.expandedGroups[groupIndex])

      // Trigger rendering group if not rendered yet
      if (!this.canRenderGroups[groupIndex]) {
        this.$set(this.canRenderGroups, groupIndex, true)
      }
    },

    /**
     * Toggle selection states of all items of
     * provided group index
     * @param {number} groupIndex
     */
    toggleSelectGroup(groupIndex) {
      const group = this.itemGroups[groupIndex]
      if (group && Array.isArray(group.items) && this.groupSelectedCounts[groupIndex] >= 0) {
        // isSubtitle is true will not be counted in the group
        const filterSubTitleItems = group.items.filter((item) => !item.isSubtitle)
        // 1) If all items have been selected, deselect all
        // 2) Otherwise, select all
        const isDeselectAll = this.groupSelectedCounts[groupIndex] === filterSubTitleItems.length
        filterSubTitleItems.forEach((item) => {
          if (item && item.value) {
            this.toggleSelectItem(
              groupIndex,
              item.value,
              isDeselectAll ? ToggleModes.DESELECT_ONLY : ToggleModes.SELECT_ONLY,
            )
          }
        })
      }
    },

    /**
     * Toggle selection state of
     * provided item and its group
     * @param {number} groupIndex
     */
    toggleSelectItem(groupIndex, item, mode = ToggleModes.DEFAULT) {
      if (this.groupSelectedCounts[groupIndex] >= 0 && item) {
        // Get item index in selected list
        const selectedIndex = this.getItemSelectedIndex(item)
        if (selectedIndex >= 0 && mode !== ToggleModes.SELECT_ONLY) {
          // Remove item if:
          // 1) already in list, and
          // 2) deselecting items are allowed
          this.inputValue.splice(selectedIndex, 1)

          // Decrement selected count of item group
          this.groupSelectedCounts[groupIndex] -= 1
        } else if (selectedIndex < 0 && mode !== ToggleModes.DESELECT_ONLY) {
          if (this.isSingleMode) {
            // Clear all
            this.inputValue.splice(0, this.inputValue.length)
            this.groupSelectedCounts.forEach((_item, index) => {
              this.groupSelectedCounts[index] = 0
            })
          }
          // Add item to list if:
          // 1) not in list, and
          // 2) selecting items are allowed
          this.inputValue.push(item.shortCode)

          // Increment selected count of item group
          this.groupSelectedCounts[groupIndex] += 1
        }
      }
    },
  },
  mixins: [InputMixin],
  props: {
    /**
     * Whether item group can be toggled together
     */
    groupSelectable: {
      default: false,
      type: Boolean,
    },

    /**
     * Single Selection Mode
     */
    isSingleMode: {
      default: false,
      type: Boolean,
    },

    /**
     * Select item groups
     */
    itemGroups: {
      default: () => [],
      type: Array,
    },

    /**
     * Function for matching selected item
     * against list item
     */
    matcher: {
      default: (a, b) => a === b,
      type: Function,
    },

    /**
     * Default input value
     */
    value: {
      default: () => [],
      type: Array,
    },
  },
  watch: {
    /**
     * Recalculate number of selected items
     * and reset expanded and render states
     * for each group
     */
    itemGroups() {
      this.initGroupSelectedCounts()
      this.initGroupExpansions()
      this.initGroupRenderStates()
    },

    /**
     * Recalculate number of selected items
     * for each group
     */
    value() {
      this.initGroupSelectedCounts()
    },
  },
}
</script>

<style lang="postcss" scoped>
.i-l__item-subtitle {
  @apply flex items-center;
  @apply h-10 pl-6 pr-2;
}

.b {
  @apply shadow-none;
  &:before {
    @apply rounded-none;
  }
  &:after {
    @apply rounded-none;
  }
}

.acc >>> .acc__items {
  @apply px-0;
}

li >>> .b.b--select {
  @apply pl-6;
}

.gp-button::v-deep {
  @apply text-black whitespace-normal;
  &.b.b--select {
    @apply px-0 py-0;
  }
}
.i-l__item .gp-button {
  &.b.b--select {
    @apply pl-6 bg-grey-50 rounded-none;
  }
}

/* List item group border */
.gp:not(:last-child) {
  @apply border-b border-solid border-grey-200;
}

.gp >>> .acc__button-icon {
  @apply absolute w-b-sm h-b-sm;
  top: calc(50% - 12px);
}

li:not(:last-child) {
  @apply border-b border-solid border-grey-200;
}

/* List item subtitle */
.i-l__item h4 {
  @apply text-sm font-medium;
}

.acc .b.b--select {
  min-height: 3rem;
}

@screen desktop {
  .acc {
    & .b.b--select >>> .b__tick {
      @apply w-b-sm h-b-sm;
    }
  }
}
@screen mobile {
  .i-l__item-subtitle {
    @apply pl-12 pr-2;
  }
  .i-l__item .gp-button {
    &.b.b--select {
      @apply pl-12;
    }
  }
}
</style>
